import React, { useState } from 'react';
import { Text } from 'react-native';
import VisibilitySensor from 'react-visibility-sensor';

import { Box } from '@styleseat/ui';
import { colors, toRGB } from '@styleseat/brand';
import {
  Button, Stack, HeaderText, BodyText,
} from '../../../../../ui';
import { InsightCardProps } from '../types';
import TimeToComplete from './TimeToComplete';
import MarketingImage from '../../../../PromoteProfile/MarketingImage';
import useAsyncState from '../../../../../../../../packages/hooks/src/useAsyncState';
import {
  shareCurried,
  downloadCurried,
  canShare,
  ShareChannel,
} from '../../../../../../modules/share';

import { MARKETING_TIPS_ACTIONS, INSIGHT_ACTIONS } from '../../../../MarketingUtils/constants';

import analytics from '../../../../../../modules/analytics';
import { postProviderInsightAction } from '../../../../../../modules/Api';
// @ts-ignore
import fontUrl from '../../../../../../../styles/fonts/SpaceGrotesk-Bold.woff';

const SHARE_POSITIVE_REVIEW_ASSET_ANALYTICS_BASE_ID = 'pro_marketing_share_positive_review_asset';

const MarketingAsset10 = require('../../../../../../../images/promote-profile/marketing-asset-10.png');
const MarketingAsset11 = require('../../../../../../../images/promote-profile/marketing-asset-11.png');

const PositiveReviewAssetCard: React.FC<InsightCardProps> = ({
  id: cardId,
  provider,
  insight,
  updateInsights,
  cardIndex,
}) => {
  const [hasTracked, setHasTracked] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const imageWidth = 190;
  const imageHeight = 190;

  const { id: insightId, metadata } = insight;
  const { text: reviewText, client_name: clientName, service_name: serviceName } = metadata;

  const formatReview = (review: string, limit: number): any[] => {
    const tagBlocks = [];
    if (!review) {
      return [review, tagBlocks];
    }
    // format the text to exclude extra space (multiple spaces, tabs, new lines, etc.)
    const inlineReview = review.replace(/(\r\n|\n|\r)/gm, '').replace(/\s\s+/g, ' ');
    if (inlineReview.length <= limit) {
      return [inlineReview, tagBlocks];
    }
    // find the truncating point (space) that is the "limit" characters or less.
    const words = inlineReview.split(' ');
    let length = 0;
    const limitedWords = [];
    words.forEach(word => {
      length += word.length + 1;
      if (length <= limit + 2) {
        limitedWords.push(word);
      }
    });
    // append [...] at the end of the text if the original text was longer than the "limit"
    if (inlineReview.length > limit) {
      limitedWords.push('[...]');
    }
    const output = limitedWords.join(' ');
    if (inlineReview.length > limit) {
      const start = output.length - 5;
      const end = output.length;
      tagBlocks.push({ start, end });
    }
    // wrap the text in double quotation marks
    const wrapped = `"${output}"`;
    return [wrapped, tagBlocks];
  };

  const [review, tagBlocks] = formatReview(reviewText, 109);

  const primaryText = [
    { text: 'Let your reviews do the work for you. Share this 5-star review from' },
    { text: clientName, color: colors.slate },
  ];

  const contentWithReview = [
    {
      text: serviceName,
      offsetBottom: 114,
      textColor: toRGB(colors.blackInBlack),
      fontSize: 10,
      fontWeight: 600,
      lineHeight: 14,
    },
    {
      text: clientName,
      offsetBottom: 56,
      textColor: toRGB(colors.blackInBlack),
      fontSize: 8,
      fontWeight: 300,
      lineHeight: 8,
    },
    {
      text: `StyleSeat.com/${provider.vanity_url}`.toUpperCase(),
      offsetBottom: 12,
      textColor: toRGB(colors.punch),
      fontSize: 10,
      lineHeight: 12,
      maxWidth: 32,
    },
    {
      text: review,
      offsetBottom: 88,
      textColor: toRGB(colors.blackInBlack),
      fontSize: 9,
      fontWeight: 400,
      lineHeight: 9,
      maxWidth: 42,
      highlightBlocks: tagBlocks,
      highlightColor: toRGB(colors.punch),
    },
  ];

  const contentWithoutReview = [
    {
      text: serviceName,
      offsetBottom: 90,
      textColor: toRGB(colors.blackInBlack),
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 14,
    },
    {
      text: clientName,
      offsetBottom: 72,
      textColor: toRGB(colors.blackInBlack),
      fontSize: 8,
      fontWeight: 300,
      lineHeight: 8,
    },
    {
      text: `StyleSeat.com/${provider.vanity_url}`.toUpperCase(),
      offsetBottom: 16,
      textColor: toRGB(colors.punch),
      fontSize: 10,
      lineHeight: 12,
      maxWidth: 32,
    },
  ];

  const content = review ? contentWithReview : contentWithoutReview;
  const background = review ? MarketingAsset10 : MarketingAsset11;

  const track = (name: String) => analytics.track(
    `${SHARE_POSITIVE_REVIEW_ASSET_ANALYTICS_BASE_ID}_${name}`,
    { review: metadata, providerId: provider.id, card_index: cardIndex },
  );

  // Ensure font is fully loaded so it can be used in the dynamic marketing images Canvas contexts
  const fontFaceLoaded = useAsyncState(async () => {
    if (fontUrl) {
      // @ts-ignore
      const face = new FontFace('SpaceGrotesk', `url(${fontUrl})`, {
        style: 'normal',
        weight: '600',
      });
      await face.load();
      return true;
    }
    return false;
  }, [fontUrl]);

  const visibilityChangeHandler = isVisible => {
    if (hasTracked) {
      return;
    }

    if (isVisible) {
      setHasTracked(true);
      track('grow_view');
    }
  };

  const onDownloadImage = useAsyncState(
    async () => {
      const params = {
        target: ShareChannel.None,
        image: imageUrl,
      };
      if (await canShare(params)) {
        // Can open generic share, prefer that over download
        return shareCurried(
          params,
          () => {
            track('share_succeeded');
          },
          () => {
            track('share_failed');
          },
        );
      }
      // Fallback for Desktop browsers + iOS Safari, who can't share images
      return downloadCurried(
        imageUrl,
        () => {
          track('download_succeeded');
        },
        () => {
          track('download_failed');
        },
      );
    }, [imageUrl],
  );

  const handleCtaClick = () => {
    postProviderInsightAction(
      provider.id,
      insightId,
      INSIGHT_ACTIONS.CTA_CLICKED,
    );
    updateInsights({
      type: MARKETING_TIPS_ACTIONS.cta,
      cardId,
    });
    track('grow_click');
    onDownloadImage();
  };

  const handleDismissClick = () => {
    postProviderInsightAction(
      provider.id,
      insightId,
      INSIGHT_ACTIONS.DISMISS_CLICKED,
    );
    updateInsights({
      type: MARKETING_TIPS_ACTIONS.dismiss,
      cardId,
    });
    track('grow_dismiss');
  };

  return (
    <VisibilitySensor onChange={visibilityChangeHandler}>
      <Box
        testID="positive-review-asset-card"
        color={colors.whiteWash}
        showBoxShadow
        borderRadius={4}
      >
        <Box
          alignItems="center"
          color={colors.lightPurple}
          style={{ width: '100%' }}
        >
          <Box
            paddingY={20}
            alignItems="center"
          >
            <MarketingImage
              assetID={cardId}
              image={background}
              width={imageWidth}
              height={imageHeight}
              isFontFaceLoaded={fontFaceLoaded}
              renderedCallback={setImageUrl}
              content={content}
            />
          </Box>
        </Box>
        <Stack
          paddingBottom={16}
          paddingTop={16}
          alignItems="center"
          space={12}
        >
          <TimeToComplete
            testId="positive-review-asset-card-time-to-complete"
            time="1 minute"
          />
          <Box
            maxWidth="100%"
            paddingTop={4}
            paddingLeft={24}
            paddingRight={24}
            testID="positive-review-asset-card-text-section"
          >
            <Text
              style={{ textAlign: 'center' }}
              testID="positive-review-asset-card-text-section-text"
            >
              {primaryText?.map(({ color, text }, index) => (
                <HeaderText
                  key={text}
                  level={2}
                  color={color || colors.blackInBlack}
                  textAlign="center"
                >
                  {index > 0 && (' ')}
                  {text}
                </HeaderText>
              ))}
            </Text>
          </Box>
          <Button
            size="small"
            onClick={handleCtaClick}
            testID="positive-review-asset-card-cta"
          >
            Post to Instagram
          </Button>
          <Button
            testID="positive-review-asset-card-dismiss"
            appearance="none"
            size="small"
            onClick={handleDismissClick}
          >
            <BodyText
              color={colors.metal}
              appearance="smaller-underline"
              textTransform="none"
            >
              No Thanks
            </BodyText>
          </Button>
        </Stack>
      </Box>
    </VisibilitySensor>
  );
};

export default PositiveReviewAssetCard;
